import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar, Info, CheckCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface MonthsAvailableStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

interface MonthsAvailable {
	january: boolean;
	february: boolean;
	march: boolean;
	april: boolean;
	may: boolean;
	june: boolean;
	july: boolean;
	august: boolean;
	september: boolean;
	october: boolean;
	november: boolean;
	december: boolean;
}

const MONTHS = [
	{ key: "january", label: "January" },
	{ key: "february", label: "February" },
	{ key: "march", label: "March" },
	{ key: "april", label: "April" },
	{ key: "may", label: "May" },
	{ key: "june", label: "June" },
	{ key: "july", label: "July" },
	{ key: "august", label: "August" },
	{ key: "september", label: "September" },
	{ key: "october", label: "October" },
	{ key: "november", label: "November" },
	{ key: "december", label: "December" },
];

export const MonthsAvailableStep = ({
	onNext,
	onBack,
	initialData,
}: MonthsAvailableStepProps) => {
	const [monthsAvailable, setMonthsAvailable] = useState<MonthsAvailable>(
		initialData?.monthsAvailable || {
			january: true,
			february: true,
			march: true,
			april: true,
			may: true,
			june: true,
			july: true,
			august: true,
			september: true,
			october: true,
			november: true,
			december: true,
		},
	);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleMonthToggle = (month: string) => {
		setMonthsAvailable((prev) => {
			const currentValue = prev[month as keyof MonthsAvailable];
			return {
				...prev,
				[month]: !currentValue,
			};
		});
	};

	const handleSelectAll = () => {
		const allSelected = Object.values(monthsAvailable).every(Boolean);
		const newValue = !allSelected;

		setMonthsAvailable({
			january: newValue,
			february: newValue,
			march: newValue,
			april: newValue,
			may: newValue,
			june: newValue,
			july: newValue,
			august: newValue,
			september: newValue,
			october: newValue,
			november: newValue,
			december: newValue,
		});
	};

	const getSelectedCount = () => {
		return Object.values(monthsAvailable).filter(Boolean).length;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			// Validate that at least one month is selected
			const selectedCount = getSelectedCount();
			if (selectedCount === 0) {
				setError(
					"Please select at least one month when you're available to take new clients.",
				);
				return;
			}

			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "months_available",
						data: {
							months_available: {
								months_available: monthsAvailable,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save month availability");
			}

			if (!result.success) {
				throw new Error(result.error || "Failed to save month availability");
			}

			onNext({ monthsAvailable });
		} catch (error) {
			console.error("Error saving month availability:", error);
			setError(
				error instanceof Error
					? error.message
					: "Failed to save month availability. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const selectedCount = getSelectedCount();
	const allSelected = selectedCount === MONTHS.length;

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 12 of 15</span>
					<span className={styles.progressLabel}>Months Available</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "80%" }} />
				</div>
			</div>

			<Card className={styles.card}>
				<CardHeader className="text-center pb-6">
					<div className={styles.iconContainer}>
						<Calendar className={styles.iconLarge} />
					</div>
					<CardTitle className={styles.cardTitle}>Months Available</CardTitle>
					<p className={`${styles.cardSubtitle} max-w-2xl mx-auto`}>
						Select the months you're currently available to take on new clients.
						Keep in mind you can always change this as your availability changes.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{error && (
							<Alert className="border-red-200 bg-red-50">
								<Info className="h-4 w-4 text-red-600" />
								<AlertDescription className="text-red-700 font-karla">
									{error}
								</AlertDescription>
							</Alert>
						)}

						<div className="space-y-6">
							{/* Quick Actions */}
							<div className={styles.quickActions}>
								<div>
									<h3 className="font-semibold text-newTheme-spruce font-karla">
										Quick Selection
									</h3>
									<p className="text-sm text-newTheme-spruce/70 font-karla">
										{selectedCount} of {MONTHS.length} months selected
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									onClick={handleSelectAll}
									className={styles.backButton}
								>
									{allSelected ? "Deselect All" : "Select All"}
								</Button>
							</div>

							{/* Month Selection Grid */}
							<div className="space-y-4">
								<h3 className="text-lg font-semibold text-newTheme-black font-karla flex items-center gap-2">
									<CheckCircle className="h-5 w-5 text-newTheme-spruce" />
									Available Months
								</h3>
								<p className="text-sm text-newTheme-black/60 font-karla">
									Check the months when you're available to take on new clients.
									Families with due dates in unchecked months won't see you in
									their search results.
								</p>

								<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
									{MONTHS.map(({ key, label }) => {
										const isChecked =
											monthsAvailable[key as keyof MonthsAvailable];
										return (
											<motion.div
												key={key}
												initial={{ opacity: 0, scale: 0.95 }}
												animate={{ opacity: 1, scale: 1 }}
												transition={{
													duration: 0.3,
													delay:
														MONTHS.findIndex((month) => month.key === key) * 0.05,
												}}
												className={`relative p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
													isChecked
														? styles.monthCardSelected
														: styles.monthCardUnselected
												}`}
												onClick={() => handleMonthToggle(key)}
											>
												<div className="flex items-center space-x-3">
													<div
														className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
															isChecked
																? styles.checkboxChecked
																: styles.checkboxUnchecked
														}`}
													>
														{isChecked && (
															<CheckCircle className="h-3 w-3 text-white" />
														)}
													</div>
													<span className="font-medium text-newTheme-black font-karla cursor-pointer flex-1">
														{label}
													</span>
												</div>
												{isChecked && (
													<div className="absolute top-2 right-2">
														<CheckCircle className="h-4 w-4 text-newTheme-spruce" />
													</div>
												)}
											</motion.div>
										);
									})}
								</div>
							</div>

							{/* Summary */}
							<div className="p-4 bg-newTheme-cotton border border-newTheme-lightBlue rounded-lg">
								<h4 className="font-semibold text-newTheme-black font-karla mb-2">
									Summary
								</h4>
								{selectedCount === MONTHS.length ? (
									<p className="text-sm text-newTheme-black/70 font-karla">
										You're available to take new clients in{" "}
										<strong>all months</strong> of the year.
									</p>
								) : selectedCount === 0 ? (
									<p className="text-sm text-red-600 font-karla">
										You haven't selected any months. Please select at least one
										month when you're available.
									</p>
								) : (
									<p className="text-sm text-newTheme-black/70 font-karla">
										You're available to take new clients in{" "}
										<strong>{selectedCount} months</strong> of the year.
										{selectedCount < 6 && (
											<span className="block mt-1 text-amber-700">
												Consider if limiting availability to fewer than 6 months
												might impact your bookings.
											</span>
										)}
									</p>
								)}
							</div>
						</div>

						{/* Navigation Buttons */}
						<div className={styles.navRowBordered}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
								disabled={loading}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading || selectedCount === 0}
								className={`${styles.submitButton} disabled:opacity-50`}
							>
								{loading ? (
									<>
										<div className={styles.spinner} />
										Saving...
									</>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
